import React from 'react'
import {
  broadcast,
  listen,
  stop_listen,
  post_gql,
  polygon_layer,
  text_layer,
  marker_layer,
  chat_layer,
  video_layer,
  INIT_POLYGON_SETTINGS,
  INIT_TEXT_SETTINGS,
  INIT_POINT_SETTINGS,
  INIT_VIDEO_SETTINGS
} from '../../utils'

import {line_layer, line_distance_layer, INIT_LINE_SETTINGS} from '../../utils/line_utils'
import {GeoJsonLayer} from '@deck.gl/layers';
import * as turf from '@turf/turf'
import './index.css'

const init_pos = [30.340354, 59.931715]

const broadcast_position = (user, mode, current) => {
  const id = `${user.id}_position`

  if (mode !== "chat")
    broadcast("remove_layer", `${id}_1`)
  switch (mode) {
    case "text":
      broadcast(
        "add_or_replace_layer",
        text_layer(user.position, {id: id, text_settings: user.text_settings})
      )
      break;
    case "marker":
      broadcast(
        "add_or_replace_layer",
        marker_layer(user.position, {id: id, point_settings: user.point_settings})
      )
      break;
    case "point":
      broadcast(
        "add_or_replace_layer",
        marker_layer(user.position, {id: id, point_settings: user.point_settings})
      )
      break;
    case "video":
      break;
    case "chat":
      const chat_layers = chat_layer(user.position, {id: id, chat_settings: {...user.chat_settings, picture: user.picture}})
      chat_layers.forEach(l => broadcast("add_or_replace_layer", l))
      break;
    default:
      if (user.polygon_settings)
        broadcast(
          "add_or_replace_layer",
          new GeoJsonLayer({
            id: id,
            data: {type: "Point", coordinates: user.position},
            pointType: 'circle+text+icon',
            getText: () => current ? "" : user.name,
            // getText: () => user.name,
            textCharacterSet: user.name.split(""),
            getTextAnchor: () => 'start',
            getAlignmentBaseline: () => 'top',
            getTextColor: [168, 0, 0, 255],
            getTextSize: 16,
            getTextPixelOffset: [35, 10],

            iconAtlas: user.picture,
            iconMapping: {"avatar":{"width":96,"y":0,"x":0,"height":96}},
            // getIcon: o => "avatar",
            getIcon: () => current ? "no_avatar" : "avatar",
            getIconSize: o => 24,
            getIconPixelOffset: [20, 10],
            iconSizeUnits: "pixels",

            textSizeUnits: 'pixels',
            getFillColor: [0, 0, 0, 0],
            getLineColor:  mode === 'cursor' || mode === 'text' ? [0, 0, 0, 0] : "#264653",
            getLineWidth: user.polygon_settings.border_width,
            lineJointRounded: true,
            pointRadiusUnits: 'meters',
            getPointRadius: mode === "line" ? user.line_settings.width : user.polygon_settings.radius * 2,
            pointRadiusScale: 0.5,
            lineCapRounded: true,
            pickable: false,
            textFontSettings: {sdf: false}
          })
        )
  }
}

class UserLayers extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      current_user: {
        id: false,
        name: false,
        position: init_pos,
        polygon_settings: INIT_POLYGON_SETTINGS,
        line_settings: INIT_LINE_SETTINGS,
        text_settings: INIT_TEXT_SETTINGS,
        point_settings: INIT_POINT_SETTINGS,
        video_settings: INIT_VIDEO_SETTINGS,
        chat_settings: {title: ""},
        edit_layer_id: false,
        l_coords: false,
        p_coords: false
      },
      layers: [],
      users: [],
      follow_user_id: false,
      listners: []
    }
  }

  update_user_settings(type, detail, settings_type){
    const current = this.state.current_user.id === detail.user_id
    var user =
      current ? this.state.current_user : this.state.users.find( u => u.id === detail.user_id)

    if (user) {
      var settings_key = null

      switch (settings_type) {
        case "text":
          settings_key = "text_settings"
          break;
        case "video":
          settings_key = "video_settings"
          break;
        case "line":
          settings_key = "line_settings"
          break;
        case "point":
          settings_key = "point_settings"
          break;
        default:
          settings_key = "polygon_settings"
      }

      const field = type.replace(`${settings_type}_`, "")
      user = {...user, [settings_key]: {...user[settings_key], [field]: detail.value}}
      this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
      broadcast_position(user, settings_type, current)
    }

    if (current)
      broadcast(`send_${type}`, detail)
  }

  update_layer_polygon_settings(type, detail){
    var obj = this.state.layers.find(v => v.id === detail.object_id).props.data
    const field = type.replace("update_polygon_", "")
    const settings = {...obj.properties.polygon_settings, [field]: detail.value}
    const props = {id: detail.object_id, coords: obj.geometry.coordinates, polygon_settings: settings}

    broadcast("add_or_replace_layer", polygon_layer(props, obj.geometry.type))
    if (this.state.current_user.id === detail.user_id)
      broadcast(`send_${type}`, detail)
  }

  update_layer_line_settings(type, detail){
    var obj = this.state.layers.find(v => v.id === detail.object_id).props.data
    const field = type.replace("update_line_", "")
    const settings = {...obj.properties.line_settings, [field]: detail.value}
    const props = {id: detail.object_id, line_coords: obj.geometry.coordinates, line_settings: settings}

    broadcast("add_or_replace_layer", line_layer(props))

    if (props.line_settings.show_distance)
      broadcast("add_or_replace_layer", line_distance_layer(`${detail.object_id}_distance`, props.line_coords))

    if (obj.properties.line_settings.show_distance && !props.line_settings.show_distance)
      broadcast("remove_layer", `${detail.object_id}_distance`)

    if (this.state.current_user.id === detail.user_id)
      broadcast(`send_${type}`, detail)
  }

  update_layer_point_settings(type, detail){
    const current = this.state.current_user.id === detail.user_id
    var obj = this.state.layers.find(v => v.id === detail.object_id).props.data
    const field = type.replace("update_point_", "")
    const settings = {...obj.properties.point_settings, [field]: detail.value}
    const props = {id: detail.object_id, point_settings: settings}

    broadcast("add_or_replace_layer", marker_layer(obj.geometry.coordinates, props))

    if (current)
      broadcast(`send_${type}`, detail)

    var user =
      current ? this.state.current_user : this.state.users.find( u => u.id === detail.user_id)

    if (user) {
      user = {...user, point_settings: settings}
      this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
    }
  }

  update_layer_text_settings(type, detail){
    const current = this.state.current_user.id === detail.user_id
    var obj = this.state.layers.find(v => v.id === detail.object_id).props.data
    const field = type.replace("update_text_", "")
    const settings = {...obj.properties.text_settings, [field]: detail.value}
    const props = {id: detail.object_id, text_settings: settings}

    broadcast("add_or_replace_layer", text_layer(obj.geometry.coordinates, props))

    if (current)
      broadcast(`send_${type}`, detail)

    var user =
      current ? this.state.current_user : this.state.users.find( u => u.id === detail.user_id)

    if (user) {
      user = {...user, text_settings: settings}
      this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
    }
  }

  update_layer_video_settings(type, detail){
    const current = this.state.current_user.id === detail.user_id
    var obj = this.state.layers.find(v => v.id === detail.object_id).props.data
    const field = type.replace("update_video_", "")
    const settings = {...obj.properties.video_settings, [field]: detail.value}
    const props = {id: detail.object_id, video_settings: settings}

    broadcast("add_or_replace_layer", video_layer(props))

    if (current)
      broadcast(`send_${type}`, detail)

    var user =
      current ? this.state.current_user : this.state.users.find( u => u.id === detail.user_id)

    if (user) {
      user = {...user, video_settings: settings}
      this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
    }
  }

  componentDidMount(){
    const listners = [
      {type: "add_or_replace_layer", fnc: ({detail}) => {
        this.setState( (state) => ({layers: [
          ...state.layers.filter(v => v.id !== detail.id),
          detail
        ]}))
      }},

      {type: "user_view_state_stop_follow", fnc: ({detail}) => this.setState({follow_user_id: false})},
      {type: "layer_edit", fnc: ({detail}) => {
        this.setState({current_user: {...this.state.current_user, edit_layer_id: detail.id}})
        broadcast("send_layer_edit", {user_id: this.state.current_user.id, object_id: detail.id})
      }},
      {type: "user_layer_edit", fnc: ({detail}) => {
        var user = this.state.users.find( u => u.id === detail.user_id)
        if (user) {
          user = {...user, edit_layer_id: detail.object_id}
          this.setState({users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
        }
      }},
      {type: "polygon_color", fnc: ({detail}) => this.update_user_settings("polygon_color", detail, "polygon")},
      {type: "polygon_radius", fnc: ({detail}) => this.update_user_settings("polygon_radius", detail, "polygon")},
      {type: "polygon_opacity", fnc: ({detail}) => this.update_user_settings("polygon_opacity", detail, "polygon")},
      {type: "polygon_border_width", fnc: ({detail}) => this.update_user_settings("polygon_border_width", detail, "polygon")},
      {type: "polygon_border_opacity", fnc: ({detail}) => this.update_user_settings("polygon_border_opacity", detail, "polygon")},
      {type: "polygon_border_color", fnc: ({detail}) => this.update_user_settings("polygon_border_color", detail, "polygon")},
      {type: "polygon_pattern", fnc: ({detail}) => this.update_user_settings("polygon_pattern", detail, "polygon")},
      {type: "polygon_pattern_scale", fnc: ({detail}) => this.update_user_settings("polygon_pattern_scale", detail, "polygon")},

      {type: "line_color", fnc: ({detail}) => this.update_user_settings("line_color", detail, "line")},
      {type: "line_opacity", fnc: ({detail}) => this.update_user_settings("line_opacity", detail, "line")},
      {type: "line_width", fnc: ({detail}) => this.update_user_settings("line_width", detail, "line")},
      {type: "line_cap_rounded", fnc: ({detail}) => this.update_user_settings("line_cap_rounded", detail, "line")},
      {type: "line_joint_rounded", fnc: ({detail}) => this.update_user_settings("line_joint_rounded", detail, "line")},
      {type: "line_show_points", fnc: ({detail}) => this.update_user_settings("line_show_points", detail, "line")},
      {type: "line_show_distance", fnc: ({detail}) => this.update_user_settings("line_show_distance", detail, "line")},

      {type: "point_icon", fnc: ({detail}) => this.update_user_settings("point_icon", detail, "point")},
      {type: "point_size", fnc: ({detail}) => this.update_user_settings("point_size", detail, "point")},
      {type: "point_angle", fnc: ({detail}) => this.update_user_settings("point_angle", detail, "point")},
      {type: "point_min_pixels", fnc: ({detail}) => this.update_user_settings("point_min_pixels", detail, "point")},
      {type: "point_max_pixels", fnc: ({detail}) => this.update_user_settings("point_max_pixels", detail, "point")},
      {type: "point_tooltip_text", fnc: ({detail}) => this.update_user_settings("tooltip_text", detail, "point")},

      {type: "text_color", fnc: ({detail}) => this.update_user_settings("text_color", detail, "text")},
      {type: "text_opacity", fnc: ({detail}) => this.update_user_settings("text_opacity", detail, "text")},
      {type: "text_text", fnc: ({detail}) => this.update_user_settings("text_text", detail, "text")},
      {type: "text_size", fnc: ({detail}) => this.update_user_settings("text_size", detail, "text")},
      {type: "text_anchor", fnc: ({detail}) => this.update_user_settings("text_anchor", detail, "text")},
      {type: "text_alignment_baseline", fnc: ({detail}) => this.update_user_settings("text_alignment_baseline", detail, "text")},
      {type: "text_angle", fnc: ({detail}) => this.update_user_settings("text_angle", detail, "text")},
      {type: "text_font_family", fnc: ({detail}) => this.update_user_settings("text_font_family", detail, "text")},
      {type: "text_font_weight", fnc: ({detail}) => this.update_user_settings("text_font_weight", detail, "text")},
      {type: "text_max_width", fnc: ({detail}) => this.update_user_settings("text_max_width", detail, "text")},
      {type: "text_word_break", fnc: ({detail}) => this.update_user_settings("text_word_break", detail, "text")},
      {type: "text_background_padding_left", fnc: ({detail}) => this.update_user_settings("text_background_padding_left", detail, "text")},
      {type: "text_background_padding_right", fnc: ({detail}) => this.update_user_settings("text_background_padding_right", detail, "text")},
      {type: "text_background_padding_top", fnc: ({detail}) => this.update_user_settings("text_background_padding_top", detail, "text")},
      {type: "text_background_padding_bottom", fnc: ({detail}) => this.update_user_settings("text_background_padding_bottom", detail, "text")},
      {type: "text_background_color", fnc: ({detail}) => this.update_user_settings("text_background_color", detail, "text")},
      {type: "text_background_opacity", fnc: ({detail}) => this.update_user_settings("text_background_opacity", detail, "text")},
      {type: "text_border_color", fnc: ({detail}) => this.update_user_settings("text_border_color", detail, "text")},
      {type: "text_border_opacity", fnc: ({detail}) => this.update_user_settings("text_border_opacity", detail, "text")},
      {type: "text_border_width", fnc: ({detail}) => this.update_user_settings("text_border_width", detail, "text")},
      {type: "text_outline_color", fnc: ({detail}) => this.update_user_settings("text_outline_color", detail, "text")},
      {type: "text_outline_opacity", fnc: ({detail}) => this.update_user_settings("text_outline_opacity", detail, "text")},
      {type: "text_outline_width", fnc: ({detail}) => this.update_user_settings("text_outline_width", detail, "text")},

      {type: "video_url", fnc: ({detail}) => this.update_user_settings("video_url", detail)},
      {type: "video_coord1", fnc: ({detail}) => this.update_user_settings("video_coord1", detail)},
      {type: "video_coord2", fnc: ({detail}) => this.update_user_settings("video_coord2", detail)},
      {type: "video_coord3", fnc: ({detail}) => this.update_user_settings("video_coord3", detail)},
      {type: "video_coord4", fnc: ({detail}) => this.update_user_settings("video_coord4", detail)},
      {type: "video_min_zoom", fnc: ({detail}) => this.update_user_settings("video_min_zoom", detail)},
      {type: "video_max_zoom", fnc: ({detail}) => this.update_user_settings("video_max_zoom", detail)},

      {type: "update_user_view_state", fnc: ({detail}) => {
        var user = this.state.users.find( u => u.id === detail.id)

        if (user) {
          user = {...user, view_state: detail}
          this.setState({users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
        }
      }},
      {type: "text_settings", fnc: ({detail}) => {
        const current = this.state.current_user.id === detail.user_id
        var user = current ? this.state.current_user : this.state.users.find( u => u.id === detail.user_id)

        if (user) {
          user = {...user, text_settings: {...detail.text_settings}}
          this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
        }

        if (current)
          broadcast(`send_text_settings`, detail)
      }},
      {type: "line_settings", fnc: ({detail}) => {
        const current = this.state.current_user.id === detail.user_id
        var user = current ? this.state.current_user : this.state.users.find( u => u.id === detail.user_id)

        if (user) {
          user = {...user, line_settings: {...detail.line_settings}}
          this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
        }

        if (current)
          broadcast(`send_line_settings`, detail)
      }},
      {type: "video_settings", fnc: ({detail}) => {
        const current = this.state.current_user.id === detail.user_id
        var user = current ? this.state.current_user : this.state.users.find( u => u.id === detail.user_id)

        if (user) {
          user = {...user, video_settings: {...detail.video_settings}}
          this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
        }

        if (current)
          broadcast(`send_video_settings`, detail)
      }},
      {type: "point_settings", fnc: ({detail}) => {
        const current = this.state.current_user.id === detail.user_id
        var user = current ? this.state.current_user : this.state.users.find( u => u.id === detail.user_id)

        if (user) {
          user = {...user, point_settings: {...detail.point_settings}}
          this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
        }

        if (current)
          broadcast(`send_point_settings`, detail)
      }},

      {type: "set_settings", fnc: ({detail}) => {
        this.setState((state) => ({
          current_user: {
            ...this.state.current_user,
            polygon_settings: detail.polygon_settings,
            line_settings: detail.line_settings,
            text_settings: detail.text_settings,
            point_settings: detail.point_settings,
            video_settings: detail.video_settings,
          }
        }))
      }},

      {type: "update_polygon_opacity", fnc: ({detail}) => this.update_layer_polygon_settings("update_polygon_opacity", detail)},
      {type: "update_polygon_color", fnc: ({detail}) => this.update_layer_polygon_settings("update_polygon_color", detail)},
      {type: "update_polygon_border_width", fnc: ({detail}) => this.update_layer_polygon_settings("update_polygon_border_width", detail)},
      {type: "update_polygon_border_opacity", fnc: ({detail}) => this.update_layer_polygon_settings("update_polygon_border_opacity", detail)},
      {type: "update_polygon_border_color", fnc: ({detail}) => this.update_layer_polygon_settings("update_polygon_border_color", detail)},
      {type: "update_polygon_pattern", fnc: ({detail}) => this.update_layer_polygon_settings("update_polygon_pattern", detail)},
      {type: "update_polygon_pattern_scale", fnc: ({detail}) => this.update_layer_polygon_settings("update_polygon_pattern_scale", detail)},

      {type: "update_line_color", fnc: ({detail}) => this.update_layer_line_settings("update_line_color", detail)},
      {type: "update_line_opacity", fnc: ({detail}) => this.update_layer_line_settings("update_line_opacity", detail)},
      {type: "update_line_width", fnc: ({detail}) => this.update_layer_line_settings("update_line_width", detail)},
      {type: "update_line_cap_rounded", fnc: ({detail}) => this.update_layer_line_settings("update_line_cap_rounded", detail)},
      {type: "update_line_joint_rounded", fnc: ({detail}) => this.update_layer_line_settings("update_line_joint_rounded", detail)},
      {type: "update_line_show_points", fnc: ({detail}) => this.update_layer_line_settings("update_line_show_points", detail)},
      {type: "update_line_show_distance", fnc: ({detail}) => this.update_layer_line_settings("update_line_show_distance", detail)},

      {type: "update_point_icon", fnc: ({detail}) => this.update_layer_point_settings("update_point_icon", detail)},
      {type: "update_point_size", fnc: ({detail}) => this.update_layer_point_settings("update_point_size", detail)},
      {type: "update_point_angle", fnc: ({detail}) => this.update_layer_point_settings("update_point_angle", detail)},
      {type: "update_point_min_pixels", fnc: ({detail}) => this.update_layer_point_settings("update_point_min_pixels", detail)},
      {type: "update_point_max_pixels", fnc: ({detail}) => this.update_layer_point_settings("update_point_max_pixels", detail)},
      {type: "update_point_tooltip_text", fnc: ({detail}) => this.update_layer_point_settings("update_point_tooltip_text", detail)},

      {type: "update_video_url", fnc: ({detail}) => this.update_layer_video_settings("update_video_url", detail)},
      {type: "update_video_coord1", fnc: ({detail}) => this.update_layer_video_settings("update_video_coord1", detail)},
      {type: "update_video_coord2", fnc: ({detail}) => this.update_layer_video_settings("update_video_coord2", detail)},
      {type: "update_video_coord3", fnc: ({detail}) => this.update_layer_video_settings("update_video_coord3", detail)},
      {type: "update_video_coord4", fnc: ({detail}) => this.update_layer_video_settings("update_video_coord4", detail)},
      {type: "update_video_min_zoom", fnc: ({detail}) => this.update_layer_video_settings("update_video_min_zoom", detail)},
      {type: "update_video_max_zoom", fnc: ({detail}) => this.update_layer_video_settings("update_video_max_zoom", detail)},

      {type: "update_text_color", fnc: ({detail}) => this.update_layer_text_settings("update_text_color", detail)},
      {type: "update_text_opacity", fnc: ({detail}) => this.update_layer_text_settings("update_text_opacity", detail)},
      {type: "update_text_text", fnc: ({detail}) => this.update_layer_text_settings("update_text_text", detail)},
      {type: "update_text_size", fnc: ({detail}) => this.update_layer_text_settings("update_text_size", detail)},
      {type: "update_text_anchor", fnc: ({detail}) => this.update_layer_text_settings("update_text_anchor", detail)},
      {type: "update_text_alignment_baseline", fnc: ({detail}) => this.update_layer_text_settings("update_text_alignment_baseline", detail)},
      {type: "update_text_angle", fnc: ({detail}) => this.update_layer_text_settings("update_text_angle", detail)},
      {type: "update_text_font_family", fnc: ({detail}) => this.update_layer_text_settings("update_text_font_family", detail)},
      {type: "update_text_font_weight", fnc: ({detail}) => this.update_layer_text_settings("update_text_font_weight", detail)},
      {type: "update_text_max_width", fnc: ({detail}) => this.update_layer_text_settings("update_text_max_width", detail)},
      {type: "update_text_word_break", fnc: ({detail}) => this.update_layer_text_settings("update_text_word_break", detail)},
      {type: "update_text_background_padding_left", fnc: ({detail}) => this.update_layer_text_settings("update_text_background_padding_left", detail)},
      {type: "update_text_background_padding_right", fnc: ({detail}) => this.update_layer_text_settings("update_text_background_padding_right", detail)},
      {type: "update_text_background_padding_top", fnc: ({detail}) => this.update_layer_text_settings("update_text_background_padding_top", detail)},
      {type: "update_text_background_padding_bottom", fnc: ({detail}) => this.update_layer_text_settings("update_text_background_padding_bottom", detail)},
      {type: "update_text_background_color", fnc: ({detail}) => this.update_layer_text_settings("update_text_background_color", detail)},
      {type: "update_text_background_opacity", fnc: ({detail}) => this.update_layer_text_settings("update_text_background_opacity", detail)},
      {type: "update_text_border_color", fnc: ({detail}) => this.update_layer_text_settings("update_text_border_color", detail)},
      {type: "update_text_border_opacity", fnc: ({detail}) => this.update_layer_text_settings("update_text_border_opacity", detail)},
      {type: "update_text_border_width", fnc: ({detail}) => this.update_layer_text_settings("update_text_border_width", detail)},
      {type: "update_text_outline_color", fnc: ({detail}) => this.update_layer_text_settings("update_text_outline_color", detail)},
      {type: "update_text_outline_opacity", fnc: ({detail}) => this.update_layer_text_settings("update_text_outline_opacity", detail)},
      {type: "update_text_outline_width", fnc: ({detail}) => this.update_layer_text_settings("update_text_outline_width", detail)},


      {type: "user_online_socket", fnc: ({detail}) => {
        if (detail.id !== this.state.current_user.id){
          this.setState({users: [
            ...this.state.users.filter(v => v.id !== detail.id),
            detail
          ]})

          post_gql(
            `query {getSettings(user_id: ${detail.id})}`,
            resp => {
              if (this.state.users.find(v => v.id === detail.id)){
                this.setState({users: [
                  ...this.state.users.filter(v => v.id !== detail.id),
                  {
                    ...resp.data.data.getSettings,
                    id: detail.id,
                    name: detail.name,
                    picture: detail.picture
                  }
                ]})
              }
            }
          )

        }
      }},
      {type: "current_user", fnc: ({detail}) => this.setState({
        current_user: {
          ...this.state.current_user,
          id: detail.id,
          name: detail.name,
          email: detail.email,
          picture: detail.picture
        }
      })},
      {type: "user_offline_socket", fnc: ({detail}) => {
        this.setState({users: [...this.state.users.filter(v => v.id !== detail.id)]})
        broadcast("remove_layer", `${detail.id}_position`)
      }},
      {type: "user_map_hover", fnc: ({detail}) => {
        var user = null
        var current = false

        if (detail.id === this.state.current_user.id){
          user = this.state.current_user
          current = true
        } else {
          user = this.state.users.find(v => v.id === detail.id)
        }

        if (user) {
          user = {...user, position: detail.coordinate}
          this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
          broadcast_position(user, detail.mode, current)

          if (current)
            broadcast("send_user_map_hover", detail)
        }
      }},
      {type: "user_map_drag_start", fnc: ({detail}) => {
        var user = null
        var current = false

        if (detail.id === this.state.current_user.id){
          user = this.state.current_user
          current = true
        } else {
          user = this.state.users.find(v => v.id === detail.id)
        }

        if (user) {
          if (detail.mode === "polygon" || detail.mode === "lastik") {
            const circle = turf.circle(detail.coordinate, user.polygon_settings.radius, {steps: 32, units: 'meters'});
            user = {...user, p_coords: circle, position: detail.coordinate}
          } else if (detail.mode === "line") {
            user = {...user, l_coords: [detail.coordinate], position: detail.coordinate}
          } else {
            if (detail.move_props) {
              user = {...user, position: detail.coordinate}
            }
          }

          this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
        }

        if (current)
          broadcast("send_user_map_drag_start", detail)
      }},
      {type: "user_map_drag", fnc: ({detail}) => {
        var user = null
        var current = false

        if (detail.id === this.state.current_user.id){
          user = this.state.current_user
          current = true
        } else {
          user = this.state.users.find(v => v.id === detail.id)
        }

        if (user) {
          if (detail.mode === "polygon" || detail.mode === "lastik"){
            var radius = user.polygon_settings.radius
            var coordinate = detail.coordinate
            var position = user.position
            var circle = turf.circle(coordinate, radius, {steps: 32, units: 'meters'});
            var circle_before = turf.circle(position, radius, {steps: 32, units: 'meters'});

            try {
              var angle = turf.bearing(position, coordinate)
              position = turf.point(position)
              coordinate = turf.point(coordinate)

              const p1 = turf.transformTranslate(position, radius, angle-90, {units: 'meters'})
              const p2 = turf.transformTranslate(position, radius, angle+90, {units: 'meters'})
              const p3 = turf.transformTranslate(coordinate, radius, angle+90, {units: 'meters'})
              const p4 = turf.transformTranslate(coordinate, radius, angle-90, {units: 'meters'})
              var poly = turf.polygon([[p1.geometry.coordinates, p2.geometry.coordinates, p3.geometry.coordinates, p4.geometry.coordinates, p1.geometry.coordinates]])
              circle = turf.union(poly, circle)
              circle = turf.union(circle_before, circle)

              user = {...user, position: detail.coordinate, p_coords: user.p_coords !== false ? turf.union(user.p_coords, circle) : circle}
              this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
            } catch (err) {
              console.log("polygon_union_error", err)
            }

            if (!user.edit_layer_id) {
              const props = {id: `${user.id}_curr_polygon`, coords: user.p_coords.geometry.coordinates, polygon_settings: user.polygon_settings}
              const layer = polygon_layer(props)

              broadcast("add_or_replace_layer", layer)
            } else {
              var obj = null
              var object = this.state.layers.find(v => v.id === user.edit_layer_id).props.data


              try {
                if (detail.mode === "polygon"){
                  obj = turf.union(object, user.p_coords, {properties: object.properties})
                } else if (detail.mode === "lastik") {
                  obj = turf.difference(object, user.p_coords, {properties: object.properties})
                }

                if (obj) {
                  const props = {id: user.edit_layer_id, coords: obj.geometry.coordinates, polygon_settings: obj.properties.polygon_settings}
                  broadcast("add_or_replace_layer", polygon_layer(props, obj.geometry.type))
                }
              } catch (err) {
                console.log("som err", err)
              }
            }

            broadcast_position(user, detail.mode, current)
          } else if (detail.mode === "line"){
            user = {...user, p_coords: false, l_coords: [...user.l_coords, detail.coordinate], position: detail.coordinate}

            if (!user.edit_layer_id) {
              const props = {id: `${user.id}_curr_line`, line_coords: user.l_coords, line_settings: user.line_settings}
              const layer = line_layer(props)

              broadcast("add_or_replace_layer", layer)

              if (props.line_settings.show_distance)
                broadcast("add_or_replace_layer", line_distance_layer(`${props.id}_distance`, props.line_coords))

              this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
            }

            broadcast_position(user, detail.mode, current)
          } else if (detail.mode === "cursor"){
            if (detail.move_props && detail.move_props.type === "video_edge") {
              user = {...user, position: detail.coordinate}
              this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})

              if (current) {
                broadcast(
                  "add_or_replace_layer",
                  new GeoJsonLayer({
                    id: `${detail.move_props.object_id}_${detail.move_props.index}_video_edge`,
                    data: turf.point(detail.coordinate, {type: "video_edge", object_id: detail.move_props.object_id, index: detail.move_props.index}),
                    getFillColor: [93, 173, 226, 200],
                    getLineWidth: 0,
                    pickable: true,
                    pointType: "circle",
                    getPointRadius: 10,
                    pointRadiusUnits: 'pixels',
                  })
                )
              }

              broadcast(
                `update_video_coord${detail.move_props.index}`,
                {
                  object_id: detail.move_props.object_id,
                  user_id: user.id,
                  value: detail.coordinate
                }
              )
            }
          }
        }

        if (current)
          broadcast("send_user_map_drag", detail)
      }},
      {type: "user_map_drag_end", fnc: ({detail}) => {
        var user = null
        var current = false

        if (detail.id === this.state.current_user.id){
          user = this.state.current_user
          current = true
        } else {
          user = this.state.users.find(v => v.id === detail.id)
        }

        if (user) {
          if (detail.mode === "polygon" || detail.mode === "lastik"){
            if (current) {
              if (!user.edit_layer_id) {
                post_gql(
                  `mutation { geomapLayerCreate(
                    geomap_id: "${window.location.pathname.replace("/t/", "")}",
                    user_id: "${user.id}",
                    type: POLYGON,
                    coords: ${JSON.stringify(user.p_coords["geometry"]["coordinates"])},
                    polygon_settings: {
                      color: "${user.polygon_settings.color}",
                      radius: ${user.polygon_settings.radius},
                      opacity: ${user.polygon_settings.opacity},
                      border_width: ${user.polygon_settings.border_width},
                      border_opacity: ${user.polygon_settings.border_opacity},
                      border_color: "${user.polygon_settings.border_color}",
                      pattern: "${user.polygon_settings.pattern}",
                      pattern_scale: ${user.polygon_settings.pattern_scale},
                    }){id user_id coords type polygon_settings { color radius opacity border_color border_width border_opacity pattern pattern_scale}}}
                  `,
                  resp => {
                    const props = resp.data.data.geomapLayerCreate
                    broadcast("add_or_replace_layer", polygon_layer(props))
                    broadcast("remove_layer", `${user.id}_curr_polygon`)
                  }
                )
              } else {
                const object = this.state.layers.find(v => v.id === user.edit_layer_id).props.data
                var coords = "[]"
                var coords_multi = "[]"

                if (object.geometry.type === "MultiPolygon") {
                  coords_multi = JSON.stringify(object.geometry.coordinates)
                } else {
                   coords= JSON.stringify(object.geometry.coordinates)
                }

                post_gql(
                  `mutation { geomapLayerUpdate(
                    geomap_id: "${window.location.pathname.replace("/t/", "")}",
                    layer_id: ${user.edit_layer_id},
                    user_id: "${user.id}",
                    coords: ${coords},
                    coords_multi: ${coords_multi},
                    polygon_settings: {
                      color: "${object.properties.polygon_settings.color}",
                      radius: ${object.properties.polygon_settings.radius},
                      opacity: ${object.properties.polygon_settings.opacity},
                      border_width: ${object.properties.polygon_settings.border_width},
                      border_opacity: ${object.properties.polygon_settings.border_opacity},
                      border_color: "${object.properties.polygon_settings.border_color}",
                      pattern: "${object.properties.polygon_settings.pattern}",
                      pattern_scale: ${object.properties.polygon_settings.pattern_scale}
                    }){id user_id coords polygon_settings { color radius opacity border_color border_width border_opacity pattern pattern_scale}}}
                  `,
                  resp => null
                )
              }
            }

            user = {...user, p_coords: false, l_coords: false, position: detail.coordinate}
            this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
          } else if (detail.mode === "line") {

            if (current) {
              if (!user.edit_layer_id) {
                post_gql(
                  `mutation { geomapLayerCreate(
                    geomap_id: "${window.location.pathname.replace("/t/", "")}",
                    user_id: "${user.id}",
                    type: LINE,
                    line_coords: ${JSON.stringify(user.l_coords)},
                    line_settings: {
                      color: "${user.line_settings.color}",
                      width: ${user.line_settings.width},
                      opacity: ${user.line_settings.opacity},
                      cap_rounded: ${user.line_settings.cap_rounded},
                      joint_rounded: ${user.line_settings.joint_rounded},
                      show_points: ${user.line_settings.show_points},
                      show_distance: ${user.line_settings.show_distance}
                    }){id user_id line_coords type line_settings { color width opacity cap_rounded joint_rounded show_points show_distance}}}
                  `,
                  resp => {
                    const props = resp.data.data.geomapLayerCreate
                    broadcast("add_or_replace_layer", line_layer(props))

                    if (props.line_settings.show_distance)
                      broadcast("add_or_replace_layer", line_distance_layer(`${props.id}_distance`, props.line_coords))

                    broadcast("remove_layer", `${user.id}_curr_line`)
                    broadcast("remove_layer", `${user.id}_curr_line_distance`)
                  }
                )
              } else {
                const object = this.state.layers.find(v => v.id === user.edit_layer_id).props.data

                post_gql(
                  `mutation { geomapLayerUpdate(
                    geomap_id: "${window.location.pathname.replace("/t/", "")}",
                    layer_id: ${user.edit_layer_id},
                    user_id: "${user.id}",
                    line_coords: ${JSON.stringify(object.geometry.coordinates)},
                    line_settings: {
                      color: "${object.properties.line_settings.color}",
                      width: ${object.properties.line_settings.width},
                      opacity: ${object.properties.line_settings.opacity},
                      cap_rounded: ${user.line_settings.cap_rounded},
                      joint_rounded: ${user.line_settings.joint_rounded},
                      show_points: ${user.line_settings.show_points},
                      show_distance: ${user.line_settings.show_distance}
                    }){id user_id coords line_settings { color width opacity cap_rounded joint_rounded show_points show_distance}}}
                  `,
                  resp => null
                )
              }
            }

            user = {...user, p_coords: false, l_coords: false, position: detail.coordinate}
            this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
          }
        }

        if (current)
          broadcast("send_user_map_drag_end", detail)
      }},
      {type: "finished_layer", fnc: ({detail}) => {
        post_gql(
          `query { geomapLayerGet(id: ${detail.layer_id}){
            id user_id point_coords coords line_coords type
            polygon_settings { color radius opacity border_color border_width border_opacity pattern pattern_scale}
            text_settings {
              color opacity text size anchor alignment_baseline angle font_family font_weight max_width word_break
              background_padding_left background_padding_right background_padding_top background_padding_bottom
              background_color background_opacity border_color border_opacity border_width
              outline_color outline_opacity outline_width
            }
            point_settings {
              icon_atlas icon_mapping icon size angle min_pixels max_pixels tooltip_text
            }
            video_settings {url coord1 coord2 coord3 coord4 min_zoom max_zoom}
            line_settings { color width opacity cap_rounded joint_rounded show_points show_distance}
            chat_settings {title picture status created_at}
          }}`,
          resp => {
            const props = resp.data.data.geomapLayerGet

            switch (props.type) {
              case "TEXT":
                broadcast("add_or_replace_layer", text_layer(props.point_coords, props))
                break;
              case "POINT":
                broadcast("add_or_replace_layer", marker_layer(props.point_coords, props))
                break;
              case "CHAT":
                chat_layer(props.point_coords, props).forEach(l => broadcast("add_or_replace_layer", l))
                break;

              case "VIDEO":
                broadcast("add_or_replace_layer", video_layer(props))
                break;

              case "LINE":
                broadcast("add_or_replace_layer", line_layer(props))

                if (props.line_settings.show_distance)
                  broadcast("add_or_replace_layer", line_distance_layer(`${props.id}_distance`, props.line_coords))

                broadcast("remove_layer", `${props.user_id}_curr_line`)
                broadcast("remove_layer", `${props.user_id}_curr_line_distance`)

                var user = null
                var current = false

                if (props.user_id === this.state.current_user.id){
                  user = this.state.current_user
                  current = true
                } else {
                  user = this.state.users.find(v => v.id === props.user_id)
                }

                if (user) {
                  user = {...user, p_coords: false, l_coords: false}
                  this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
                }
                break;
              default:
                broadcast("add_or_replace_layer", polygon_layer(props))
                broadcast("remove_layer", `${props.user_id}_curr_polygon`)

                user = null
                current = false

                if (props.user_id === this.state.current_user.id){
                  user = this.state.current_user
                  current = true
                } else {
                  user = this.state.users.find(v => v.id === props.user_id)
                }

                if (user) {
                  user = {...user, p_coords: false, l_coords: false}
                  this.setState(current ? {current_user: user} : {users: [...this.state.users.map(v => v.id !== user.id ? v : user)]})
                }
            }
          }
        )
      }},
    ]

    listners.forEach(l => listen(l.type, l.fnc))
    this.setState({listners})
  }

  componentWillUnmount(){this.state.listners.forEach(l => stop_listen(l.type, l.fnc))}

  render() {
    return(
      <div className="users-cont"> {
        this.state.current_user.name && <div className="curr-name">
          <img
            style={{height: "32px", width: "32px"}}
            src={this.state.current_user.picture}
            alt=""
          />
          <span
            style={{padding: "0px 10px"}}
          >{this.state.current_user.name}</span>
        </div>
      }
      {
        this.state.users.map(u =>
          <img
            key={`user_${u.id}`}
            className={this.state.follow_user_id === u.id ? `user_avatar followed` : `user_avatar`}
            src={u.picture}
            alt=""
            onClick={() => {
              if (this.state.follow_user_id === u.id) {
                this.setState({follow_user_id: false})
                broadcast("user_avatar_click", {id: false})
              } else {
                this.setState({follow_user_id: u.id})
                broadcast("user_avatar_click", {id: u.id, view_state: this.state.users.find(v => v.id === u.id).view_state})
              }
            }}
          />
        )
      }
      <span className="add-users-link">+</span>
      </div>
    )
  }
}

export default UserLayers;
